// ═══════════════════════════════════════════════════════════
// About Section — السيرة الذاتية
// Biography, timeline, awards & literary memberships
// Content pulled from Supabase
// ═══════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import '../styles/About.css';
import defaultAboutImage from '../assets/images/about-portrait.jpg';

// Fallback data
const DEFAULT_BIO = [
  'شاعرٌ لبنانيٌّ معاصر، وُلد في عماطور — الشوف من جبل لبنان. يمتلك رصيدًا أدبيًا غنيًا يتمثّل في عشرة دواوين شعر كلاسيكي، تنوّعت موضوعاتها بين الوجدان، والهمّ الإنساني والاجتماعي، والقضية الوطنية، والغزل.',
  'تعبّر قصائده عن الإنسان في أفراحه وأوجاعه، وعن الوطن في قلقه وأحلامه، بلغة صادقة وإحساسٍ مرهف يجمع بين البساطة والعمق ويصل مباشرة إلى وجدان القارئ.',
  'شارك في المئات من الأمسيات والندوات الشعرية في بيروت ومعظم المناطق اللبنانية، والدول العربية — أبو ظبي، الشارقة، دبي، قطر، الأردن — والآسيوية في كازاخستان، إضافة إلى أستراليا بمعظم ولاياتها. كما كانت له العشرات من الإطلالات والمقابلات التلفزيونية والإذاعية.',
  'إلى جانب عطائه الشعري، يضطلع بدور ثقافي فاعل، إذ يشغل منصب رئيس منتدى حروف الثقافي، ورئيس اللقاء الثقافي في عماطور، وهو عضو الهيئة الإدارية في اتحاد الكتّاب اللبنانيين. إضافة إلى أنه كان عضواً في المجلس المذهبي لطائفة الموحدين الدروز في لبنان.',
];

const DEFAULT_TIMELINE = [
  { year: '1963', event: 'وُلد في عماطور الشوف — جبل لبنان' },
  { year: '1980s', event: 'تلقّى علومه الأولى في بيروت ثم في الجبل' },
  { year: '1988', event: 'تخرّج من كلية العلوم المالية والاقتصادية — إجازة في إدارة الأعمال' },
  { year: '1990', event: 'هاجر إلى أستراليا وتابع دراسته في الاقتصاد والعلوم المالية والكومبيوتر' },
  { year: '2000s', event: 'أسّس مع مثقفين لبنانيين وعرب «حركة الحضارة العربية» و«صالون الأربعاء الثقافي»' },
  { year: '2011', event: 'إصدار ديوان «بوح السنابل» — الطبعة الأولى' },
  { year: '2020s', event: 'إصدار دواوين: «لحظ في عيون المساء»، «لم يبقَ لي»، «إيقاعات ممنوعة»' },
  { year: '2024', event: 'مشاركة في فعاليات أدبية في الأردن — قصيدة «شكر وامتنان» و«دوحة الشرق»' },
];

const DEFAULT_MEMBERSHIPS = [
  { title: 'منتدى حروف الثقافي', role: 'رئيس المنتدى', icon: '✒️' },
  { title: 'اللقاء الثقافي في عماطور', role: 'رئيس اللقاء', icon: '🏛️' },
  { title: 'اتحاد الكتّاب اللبنانيين', role: 'عضو الهيئة الإدارية', icon: '📜' },
  { title: 'المجلس المذهبي لطائفة الموحدين الدروز', role: 'عضو سابق', icon: '⚖️' },
  { title: 'غرفة التجارة والصناعة اللبنانية الأسترالية', role: 'عضو مجلس إدارة وممثل في لبنان', icon: '🏢' },
  { title: 'المنتدى الأدبي للبنان المهاجر', role: 'عضو مؤسس', icon: '📚' },
  { title: 'نادي الثقافة اللبنانية', role: 'عضو — جنوب أستراليا', icon: '🌿' },
  { title: 'حركة الحضارة العربية', role: 'مؤسس مشارك', icon: '🌍' },
  { title: 'صالون الأربعاء الثقافي', role: 'مؤسس مشارك', icon: '☕' },
];

// Custom hook for scroll reveal
function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return [ref, visible];
}

function About() {
  const [bioRef, bioVisible] = useReveal();
  const [timeRef, timeVisible] = useReveal();
  const [memberRef, memberVisible] = useReveal();

  // Data from Supabase
  const [aboutImage, setAboutImage] = useState(defaultAboutImage);
  const [poetName, setPoetName] = useState('الشاعر وجدي عبد الصمد');
  const [bioParagraphs, setBioParagraphs] = useState(DEFAULT_BIO);
  const [timeline, setTimeline] = useState(DEFAULT_TIMELINE);
  const [memberships, setMemberships] = useState(DEFAULT_MEMBERSHIPS);

  // Fetch all about content from Supabase
  useEffect(() => {
    const fetchContent = async () => {
      try {
        // Fetch site_content, timeline, memberships in parallel
        const [contentRes, timelineRes, membershipsRes] = await Promise.all([
          supabase.from('site_content').select('about_image_url, poet_name, bio_paragraphs').eq('id', 1).single(),
          supabase.from('timeline').select('*').order('sort_order'),
          supabase.from('memberships').select('*').order('sort_order'),
        ]);

        if (contentRes.data) {
          if (contentRes.data.about_image_url) setAboutImage(contentRes.data.about_image_url);
          if (contentRes.data.poet_name) setPoetName(contentRes.data.poet_name);
          if (contentRes.data.bio_paragraphs && contentRes.data.bio_paragraphs.length > 0) {
            setBioParagraphs(contentRes.data.bio_paragraphs);
          }
        }

        if (timelineRes.data && timelineRes.data.length > 0) {
          setTimeline(timelineRes.data);
        }

        if (membershipsRes.data && membershipsRes.data.length > 0) {
          setMemberships(membershipsRes.data);
        }
      } catch (_) {
        // Use defaults
      }
    };

    fetchContent();

    // Listen for realtime updates
    const channels = [
      supabase.channel('site_content_about')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'site_content' }, () => fetchContent())
        .subscribe(),
      supabase.channel('timeline_changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'timeline' }, () => fetchContent())
        .subscribe(),
      supabase.channel('memberships_changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'memberships' }, () => fetchContent())
        .subscribe(),
    ];

    return () => { channels.forEach(ch => supabase.removeChannel(ch)); };
  }, []);

  return (
    <section className="about" id="about">
      <div className="about__container">
        {/* Section Title */}
        <div className="section-title">
          <h2>السيرة الذاتية</h2>
          <p>رحلة شاعر من جبل لبنان إلى العالم</p>
          <div className="title-line" />
        </div>

        {/* ─── Biography ──────────────────────────────── */}
        <div
          ref={bioRef}
          className={`about__bio reveal ${bioVisible ? 'visible' : ''}`}
        >
          <div className="about__bio-content">
            <div className="about__bio-image-wrapper">
              <img
                src={aboutImage}
                alt={poetName}
                className="about__bio-image"
                loading="lazy"
              />
            </div>
            <div className="about__bio-text">
              <div className="about__bio-quote-mark">❞</div>
              <h3 className="about__bio-name">{poetName}</h3>
              <p className="about__bio-subtitle">
                شاعر لبناني معاصر — عماطور، الشوف
                <span className="about__bio-subtitle-en">Lebanese Contemporary Poet — Amatour, Chouf</span>
              </p>
              {bioParagraphs.map((paragraph, i) => (
                <p key={i} className="about__bio-paragraph">{paragraph}</p>
              ))}
            </div>
          </div>
        </div>

        {/* Ornament */}
        <div className="ornament-divider">✦ ✦ ✦</div>

        {/* ─── Timeline ───────────────────────────────── */}
        <div
          ref={timeRef}
          className={`about__timeline-section reveal ${timeVisible ? 'visible' : ''}`}
        >
          <h3 className="about__section-heading">المسيرة الأدبية</h3>
          <div className="about__timeline">
            <div className="about__timeline-line" />
            {timeline.map((item, i) => (
              <div
                className={`about__timeline-item ${i % 2 === 0 ? 'about__timeline-item--right' : 'about__timeline-item--left'}`}
                key={item.id || i}
                style={{ animationDelay: `${i * 150}ms` }}
              >
                <div className="about__timeline-dot" />
                <div className="about__timeline-card card">
                  <span className="about__timeline-year">{item.year}</span>
                  <p className="about__timeline-event">{item.event}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Ornament */}
        <div className="ornament-divider">✦ ✦ ✦</div>

        {/* ─── Memberships ────────────────────────────── */}
        <div
          ref={memberRef}
          className={`about__memberships-section reveal ${memberVisible ? 'visible' : ''}`}
        >
          <h3 className="about__section-heading">العضويات والمؤسسات</h3>
          <div className="about__memberships-grid">
            {memberships.map((item, i) => (
              <div className="about__membership-card card" key={item.id || i}>
                <span className="about__membership-icon">{item.icon}</span>
                <h4 className="about__membership-title">{item.title}</h4>
                <p className="about__membership-role">{item.role}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default About;